import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Card, 
  PageTitle, 
  Spinner, 
  EmptyState 
} from '../components';
import { 
  FaFilter, 
  FaCheck, 
  FaClock, 
  FaExclamationCircle,
  FaHistory
} from 'react-icons/fa';
import changeService from '../services/changeService';
import { formatRelativeTime, formatImportance } from '../utils/formatters';

const Changes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [changes, setChanges] = useState([]);
  const [error, setError] = useState(null);
  const [totalChanges, setTotalChanges] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    isRead: location.search.includes('isRead=false') ? false : null,
    siteId: null,
    importance: null,
    dateFrom: null,
    dateTo: null
  });

  const PAGE_SIZE = 10;

  // 获取变更数据
  useEffect(() => {
    const fetchChanges = async () => {
      try {
        setLoading(true);
        
        // 转换过滤条件为API参数
        const params = {
          page: currentPage,
          limit: PAGE_SIZE,
          sort: '-createdAt'
        };
        
        // 添加活跃的过滤条件
        if (filters.isRead !== null) {
          params.isRead = filters.isRead;
        }
        if (filters.siteId) {
          params.siteId = filters.siteId;
        }
        if (filters.importance) {
          params.importance = filters.importance;
        }
        if (filters.dateFrom) {
          params.dateFrom = filters.dateFrom;
        }
        if (filters.dateTo) {
          params.dateTo = filters.dateTo;
        }
        
        // 请求数据
        const result = await changeService.getChanges(params);
        
        setChanges(result.changes || []);
        setTotalChanges(result.total || 0);
      } catch (err) {
        console.error('获取变更失败', err);
        setError('获取变更数据失败，请刷新页面重试');
      } finally {
        setLoading(false);
      }
    };
    
    fetchChanges();
  }, [currentPage, filters]);

  // 页面变化处理
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 应用过滤器
  const applyFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // 重置到第一页
    
    // 更新URL参数
    const searchParams = new URLSearchParams();
    const combinedFilters = { ...filters, ...newFilters };
    
    if (combinedFilters.isRead !== null) {
      searchParams.set('isRead', combinedFilters.isRead);
    }
    if (combinedFilters.siteId) {
      searchParams.set('siteId', combinedFilters.siteId);
    }
    if (combinedFilters.importance) {
      searchParams.set('importance', combinedFilters.importance);
    }
    
    navigate({ search: searchParams.toString() });
  };

  // 标记已读/未读处理
  const handleMarkRead = async (changeId, isRead) => {
    try {
      await changeService.markChangeRead(changeId, isRead);
      
      // 更新本地状态
      setChanges(prevChanges => 
        prevChanges.map(change => 
          change._id === changeId ? { ...change, isRead } : change
        )
      );
    } catch (err) {
      console.error('标记变更状态失败', err);
    }
  };
  
  // 清除过滤器
  const clearFilters = () => {
    setFilters({
      isRead: null,
      siteId: null,
      importance: null,
      dateFrom: null,
      dateTo: null
    });
    navigate({ search: '' });
  };

  // 如果正在加载，显示加载中状态
  if (loading && currentPage === 1) {
    return <Spinner fullPage text="加载变更记录..." />;
  }

  // 计算总页数
  const totalPages = Math.ceil(totalChanges / PAGE_SIZE);

  return (
    <div className="changes-page">
      <PageTitle 
        title="变更记录" 
        subtitle="查看所有监控站点的内容变更记录"
        icon={<FaHistory />}
      />
      
      {/* 过滤器面板 */}
      <Card className="mb-4">
        <div className="filter-panel">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">读取状态</label>
              <select 
                className="form-select"
                value={filters.isRead === null ? '' : filters.isRead ? 'true' : 'false'}
                onChange={(e) => applyFilters({ isRead: e.target.value === '' ? null : e.target.value === 'true' })}
              >
                <option value="">全部</option>
                <option value="false">未读</option>
                <option value="true">已读</option>
              </select>
            </div>
            
            <div className="col-md-3">
              <label className="form-label">重要性</label>
              <select 
                className="form-select"
                value={filters.importance || ''}
                onChange={(e) => applyFilters({ importance: e.target.value || null })}
              >
                <option value="">全部</option>
                <option value="5">非常重要</option>
                <option value="4">重要</option>
                <option value="3">中等</option>
                <option value="2">一般</option>
                <option value="1">不重要</option>
              </select>
            </div>
            
            <div className="col-md-6 d-flex align-items-end">
              <button 
                className="btn btn-outline-secondary me-2"
                onClick={clearFilters}
              >
                清除过滤器
              </button>
            </div>
          </div>
        </div>
      </Card>
      
      {/* 错误提示 */}
      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          <FaExclamationCircle className="me-2" />
          {error}
        </div>
      )}
      
      {/* 变更列表 */}
      <Card>
        {changes.length > 0 ? (
          <>
            <div className="list-group list-group-flush">
              {changes.map(change => (
                <div 
                  key={change._id}
                  className="list-group-item list-group-item-action p-3"
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="me-auto">
                      <div className="d-flex align-items-center mb-1">
                        <span 
                          className={`badge me-2 ${change.isRead ? 'bg-secondary' : 'bg-danger'}`}
                        >
                          {change.isRead ? '已读' : '未读'}
                        </span>
                        <h5 className="mb-0">
                          {change.site?.name || '未知站点'} - {change.pageUrl.split('/').pop() || change.pageUrl}
                        </h5>
                      </div>
                      <p className="text-muted mb-0">
                        <span 
                          className="me-2 importance-indicator d-inline-block"
                          style={{ 
                            backgroundColor: `var(--bs-${change.importance >= 4 ? 'danger' : (change.importance >= 3 ? 'warning' : 'success')})`,
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%'
                          }}
                        ></span>
                        {formatImportance(change.importance)} · {formatRelativeTime(change.createdAt)} · 
                        URL: <a href={change.pageUrl} target="_blank" rel="noopener noreferrer" className="ms-1">{change.pageUrl}</a>
                      </p>
                    </div>
                    <div>
                      <button 
                        className={`btn btn-sm ${change.isRead ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                        onClick={() => handleMarkRead(change._id, !change.isRead)}
                      >
                        {change.isRead ? (
                          <>
                            <FaClock className="me-1" /> 标为未读
                          </>
                        ) : (
                          <>
                            <FaCheck className="me-1" /> 标为已读
                          </>
                        )}
                      </button>
                      <Link 
                        to={`/changes/${change._id}`}
                        className="btn btn-sm btn-primary ms-2"
                      >
                        查看详情
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* 分页控件 */}
            {totalPages > 1 && (
              <div className="pagination-container d-flex justify-content-center mt-4">
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      上一页
                    </button>
                  </li>
                  
                  {[...Array(totalPages).keys()].map(page => (
                    <li 
                      key={page + 1} 
                      className={`page-item ${currentPage === page + 1 ? 'active' : ''}`}
                    >
                      <button 
                        className="page-link"
                        onClick={() => handlePageChange(page + 1)}
                      >
                        {page + 1}
                      </button>
                    </li>
                  ))}
                  
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      下一页
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </>
        ) : (
          <EmptyState 
            type="no-data"
            title="暂无变更记录"
            message={
              Object.values(filters).some(v => v !== null) 
                ? "没有符合筛选条件的变更记录，请尝试调整筛选条件。" 
                : "您的监控站点尚未发现任何变更。"
            }
            action={
              Object.values(filters).some(v => v !== null) && (
                <button 
                  className="btn btn-primary"
                  onClick={clearFilters}
                >
                  <FaFilter className="me-2" />
                  清除所有筛选
                </button>
              )
            }
          />
        )}
      </Card>
    </div>
  );
};

export default Changes; 